import { observer } from 'mobx-react'
import { FC, useState } from 'react'
import './Buttons.css'
import { Button, Popup } from '@components/Generic'
import { LoadingSpinner } from '@components/Generic'
import { forceRedirect } from '@/methods/forceRedirect'
import { API_URL } from '@/constants/apiUrl'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { cx } from '@/utils/cx'

export interface TransRejectProps {
  rejectButtonText?: string
  rejectPopupTitle?: string
  backButtonText?: string
  confirmButtonText?: string
}

interface RejectButtonProps {
  btnDisabled: boolean
  transReject: TransRejectProps
  className?: string
}

interface GenerateRedirectUrlResponse {
  redirectUrl: string
}

export const RejectButton: FC<RejectButtonProps> = observer(
  ({ btnDisabled, transReject, className }) => {
    const [btnLoading, setBtnLoading] = useState<boolean>(false)
    const [rejectReason, setRejectReason] = useState<string | undefined>()
    const [closeOngoing, setCloseOngoing] = useState<boolean>(false)
    const [popupVisible, setPopupVisible] = useState<boolean>(false)

    const handleReject = async () => {
      setCloseOngoing(true)

      try {
        setBtnLoading(true)

        const { data } = await axiosInstance.post<GenerateRedirectUrlResponse>(
          `${API_URL}/electronic-id/reject-signing`,
          { rejectReason },
          {
            withCredentials: true,
          }
        )

        forceRedirect(data.redirectUrl)
      } catch (error) {
        devLog('error: ', error)
      }

      setBtnLoading(false)
      setCloseOngoing(false)
      setPopupVisible(false)
    }

    const handleOpenRejectPopup = () => {
      setPopupVisible(true)
    }

    const handleCloseRejectPopup = () => {
      setPopupVisible(false)
    }

    return (
      <>
        <Popup
          visibility={popupVisible}
          handleVisibility={handleCloseRejectPopup}
          closeIconHidden={true}
          clickOutsideEnabled={!closeOngoing}
          title={transReject.rejectPopupTitle}
          className="feedback-popup"
          footer={
            <>
              <Button
                onClick={handleCloseRejectPopup}
                disabled={closeOngoing}
                className="button--secondary feedback-popup__button"
              >
                {transReject.backButtonText}
              </Button>
              <Button
                onClick={handleReject}
                disabled={!rejectReason?.trim() || closeOngoing}
                className="feedback-popup__button"
              >
                {transReject.confirmButtonText}
              </Button>
            </>
          }
        >
          <textarea
            className="feedback-popup__textarea"
            name="rejectReason"
            rows={6}
            maxLength={1000}
            placeholder={transReject.rejectPopupTitle}
            aria-label={transReject.rejectPopupTitle}
            aria-required="true"
            value={rejectReason ?? ''}
            required
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div
            className={cx(
              'feedback-popup__char-count',
              (rejectReason?.length ?? 0) >= 1000 && 'feedback-popup__char-count--limit'
            )}
          >
            {rejectReason?.length ?? 0}/1000
          </div>
        </Popup>
        <Button
          onClick={handleOpenRejectPopup}
          className={cx('button--secondary', className)}
          disabled={btnDisabled || btnLoading}
        >
          {btnLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', btnLoading && 'generic__button-text--loading')}
          >
            {transReject.rejectButtonText}
          </span>
        </Button>
      </>
    )
  }
)
